import { useEffect, useRef } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { InboundChange } from "@/models/legislation/metadata/ConsolidationMetadata";
import { parseInboundChange } from "@/lib/consolidationLegislationUtils";
import { PATH_KEYS } from "@/services/appServices";
import { useItemService } from "./useServices";
import { restError } from "@/models/client/RestResponse";

export type ConsolidationTimelineWindowMetadata = {
  anchorLawExternalId: string;
  inboundChanges: InboundChange[];
};

export type ConsolidationTimelineWindows = {
  windows: ConsolidationTimelineWindowMetadata[];
  /** Inbound changes keyed by their window anchor accession. */
  byAnchor: Map<string, InboundChange[]>;
  /** Flattened inbound changes across every window on the timeline. */
  allInboundChanges: InboundChange[];
};

type UseConsolidationTimelineMetadataOptions = {
  /** Family timeline root (hovedlov) accession; every LBK window shares it. */
  rootExternalId: string | undefined;
  /** Loaded document accession when timeline_gold may live on a non-root row. */
  timelineFallbackExternalId?: string;
  enabled?: boolean;
};

const normalizeInboundChanges = (
  raw: Array<Record<string, unknown>>
): InboundChange[] =>
  raw
    .map((entry) => parseInboundChange(entry))
    .filter((entry): entry is InboundChange => entry !== null);

/** Stable React Query key for family-scoped timeline window metadata. */
export const consolidationTimelineMetadataQueryKey = (
  rootExternalId: string | undefined
) =>
  [PATH_KEYS.consolidation, "timeline-window-metadata", rootExternalId] as const;

/**
 * Fetches inbound consolidation metadata for every LBK window on a family
 * timeline so the timeline can plot consolidation diamonds across all windows,
 * regardless of which point-in-time / document is currently loaded.
 */
export const useConsolidationTimelineMetadata = ({
  rootExternalId,
  timelineFallbackExternalId,
  enabled = true,
}: UseConsolidationTimelineMetadataOptions) => {
  const consolidationService = useItemService(
    (services) => services.consolidation
  );
  const lastFallbackRefetchKeyRef = useRef<string | null>(null);

  const query = useQuery<ConsolidationTimelineWindows>({
    queryKey: consolidationTimelineMetadataQueryKey(rootExternalId),
    queryFn: async ({ signal }) => {
      const response = await consolidationService.getTimelineWindowMetadata(
        rootExternalId!,
        { timelineFallbackExternalId },
        signal
      );
      if (!response.success) {
        throw restError(response);
      }

      const windows: ConsolidationTimelineWindowMetadata[] =
        response.value.windows.map((window) => ({
          anchorLawExternalId: window.anchorLawExternalId,
          inboundChanges: normalizeInboundChanges(window.inboundChanges ?? []),
        }));

      const byAnchor = new Map<string, InboundChange[]>();
      const allInboundChanges: InboundChange[] = [];
      for (const window of windows) {
        byAnchor.set(window.anchorLawExternalId, window.inboundChanges);
        allInboundChanges.push(...window.inboundChanges);
      }

      return { windows, byAnchor, allInboundChanges };
    },
    enabled: Boolean(enabled && rootExternalId?.trim()),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { data, isFetching, refetch } = query;
  const inboundChangeCount = data?.allInboundChanges.length ?? 0;

  useEffect(() => {
    lastFallbackRefetchKeyRef.current = null;
  }, [rootExternalId]);

  useEffect(() => {
    if (!enabled || !rootExternalId?.trim()) return;

    const fallback = timelineFallbackExternalId?.trim();
    if (!fallback || fallback === rootExternalId.trim()) return;
    if (isFetching) return;
    if (inboundChangeCount > 0) return;

    const refetchKey = `${rootExternalId}:${fallback}`;
    if (lastFallbackRefetchKeyRef.current === refetchKey) return;

    lastFallbackRefetchKeyRef.current = refetchKey;
    void refetch();
  }, [
    enabled,
    inboundChangeCount,
    isFetching,
    refetch,
    rootExternalId,
    timelineFallbackExternalId,
  ]);

  return query;
};
